import React, { useState, useEffect } from 'react';
import { getLoads, getColours, getDashboardAnalytics, getFieldStatus } from '../../lib/treeApi';
import { useTreeAuth } from '../../lib/treeAuth';
import {
  Package, TreePine, Truck, Clock,
  RefreshCw, CheckCircle, AlertTriangle, XCircle,
  Calendar, MapPin, ArrowRight, TrendingUp, Building2, Tv
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { registerColours, colourHex, swatchStyle } from '../../lib/treeColors';

export default function TreeDashboardPage() {
  const { user, settings } = useTreeAuth();
  const navigate = useNavigate();
  const [loads, setLoads] = useState([]);
  const [colours, setColours] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [fieldStatus, setFieldStatus] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [loadsRes, coloursRes, analyticsRes, fsRes] = await Promise.all([
        getLoads(), getColours(), getDashboardAnalytics(), getFieldStatus()
      ]);
      setLoads(loadsRes.data);
      setColours(coloursRes.data);
      registerColours(coloursRes.data);
      setAnalytics(analyticsRes.data);
      const statusMap = {};
      (fsRes.data || []).forEach(s => { statusMap[s.color] = s.quantity || 0; });
      setFieldStatus(statusMap);
    } catch (err) {
      console.error('Failed to fetch', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const getStockForColour = (color) => colours.find(c => c.color === color?.toLowerCase())?.quantity || 0;
  const totalStock = colours.reduce((sum, c) => sum + (c.quantity || 0), 0);

  const upcomingCollections = loads
    .filter(l => l.status === 'loading')
    .sort((a, b) => (a.departure_date || '').localeCompare(b.departure_date || ''));

  const now = new Date();
  const in7days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  const next7dayCollections = upcomingCollections.filter(l => {
    if (!l.departure_date) return false;
    const d = new Date(l.departure_date + 'T00:00:00');
    return d <= in7days;
  });

  const stockRequired = {};
  next7dayCollections.forEach(load => {
    (load.pallets_required || []).forEach(p => {
      const color = p.color?.toLowerCase();
      if (!color) return;
      if (!stockRequired[color]) stockRequired[color] = 0;
      stockRequired[color] += p.quantity;
    });
  });

  const fulfillmentSummary = Object.entries(stockRequired).map(([color, needed]) => {
    const available = getStockForColour(color);
    const shortfall = Math.max(0, needed - available);
    return { color, needed, available, shortfall, ok: shortfall === 0 };
  });

  const allFulfilled = fulfillmentSummary.every(f => f.ok);
  const totalNeeded = fulfillmentSummary.reduce((sum, f) => sum + f.needed, 0);
  const totalShortfall = fulfillmentSummary.reduce((sum, f) => sum + f.shortfall, 0);

  const FULL_LOAD = settings.pallets_per_trailer;
  const totalFieldReady = colours.reduce((s, c) => s + (fieldStatus[c.color] || 0), 0);
  const fullLoads = Math.floor(totalFieldReady / FULL_LOAD);
  const fieldRemainder = totalFieldReady % FULL_LOAD;

  const checkCollectionFulfillment = (pallets_required) => {
    if (!pallets_required?.length) return 'none';
    for (const p of pallets_required) { if (getStockForColour(p.color) < p.quantity) return 'need_more'; }
    return 'can_fulfill';
  };

  return (
    <div className="space-y-6" data-testid="tree-dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#0A1711] font-['Outfit'] tracking-tight">Dashboard</h1>
          <p className="text-sm text-[#4A5D53] mt-1">Welcome back, {user?.name || 'Admin'} — Mains of Corsindae</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => navigate('/trees/tv-dashboard')} variant="outline" size="sm" className="border-[#1A4331]/20 text-[#1A4331]" data-testid="tree-tv-mode-button">
            <Tv className="w-4 h-4 mr-2" /> TV Mode
          </Button>
          <Button onClick={fetchAll} variant="outline" size="sm" className="border-[#1A4331]/20 text-[#1A4331]" data-testid="tree-refresh-dashboard">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><RefreshCw className="w-6 h-6 animate-spin text-[#1A4331]" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="tree-stats-grid">
            <div className="stat-card cursor-pointer" onClick={() => navigate('/trees/pallets')}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[9px] uppercase tracking-[0.15em] font-bold text-[#4A5D53]">In Stock</span>
                <Package className="w-3.5 h-3.5 text-[#1A4331]" />
              </div>
              <p className="text-xl font-bold text-[#0A1711] font-['Outfit']">{totalStock}</p>
              <p className="text-[10px] text-[#4A5D53]">{(totalStock * settings.trees_per_pallet).toLocaleString()} trees</p>
            </div>
            <div className="stat-card cursor-pointer" onClick={() => navigate('/trees/loads')}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[9px] uppercase tracking-[0.15em] font-bold text-[#4A5D53]">Next 7 Days</span>
                <TreePine className="w-3.5 h-3.5 text-[#E27B58]" />
              </div>
              <p className="text-xl font-bold text-[#0A1711] font-['Outfit']">{totalNeeded}</p>
              <p className="text-[10px] text-[#4A5D53]">pallets for {next7dayCollections.length} collection{next7dayCollections.length !== 1 ? 's' : ''}</p>
            </div>
            <div className="stat-card cursor-pointer" onClick={() => navigate('/trees/pallets')}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[9px] uppercase tracking-[0.15em] font-bold text-[#4A5D53]">Status</span>
                {allFulfilled ? <CheckCircle className="w-3.5 h-3.5 text-emerald-600" /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />}
              </div>
              <p className={`text-base font-bold font-['Outfit'] ${allFulfilled ? 'text-emerald-700' : 'text-amber-700'}`}>
                {fulfillmentSummary.length === 0 ? 'No Orders' : allFulfilled ? 'All Covered' : `Need ${totalShortfall}`}
              </p>
              <p className="text-[10px] text-[#4A5D53]">{allFulfilled ? 'stock OK' : 'from field'}</p>
            </div>
            <div className="stat-card cursor-pointer" onClick={() => navigate('/trees/loads')}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[9px] uppercase tracking-[0.15em] font-bold text-[#4A5D53]">Upcoming</span>
                <Truck className="w-3.5 h-3.5 text-[#1A4331]" />
              </div>
              <p className="text-xl font-bold text-[#0A1711] font-['Outfit']">{upcomingCollections.length}</p>
              <p className="text-[10px] text-[#4A5D53]">collections</p>
            </div>
            <div className="stat-card">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[9px] uppercase tracking-[0.15em] font-bold text-[#4A5D53]">Avg Yard Time</span>
                <Clock className="w-3.5 h-3.5 text-blue-500" />
              </div>
              <p className="text-xl font-bold text-[#0A1711] font-['Outfit']">{analytics?.avg_yard_days || 0}</p>
              <p className="text-[10px] text-[#4A5D53]">days per pallet</p>
            </div>
            <div className="stat-card">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[9px] uppercase tracking-[0.15em] font-bold text-[#4A5D53]">Total Moved</span>
                <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
              </div>
              <p className="text-xl font-bold text-[#0A1711] font-['Outfit']">{analytics?.total_out || 0}</p>
              <p className="text-[10px] text-[#4A5D53]">{((analytics?.total_out || 0) * settings.trees_per_pallet).toLocaleString()} trees dispatched</p>
            </div>
          </div>

          <div className={`rounded-xl p-4 border-2 cursor-pointer transition-colors ${totalFieldReady >= FULL_LOAD ? 'bg-emerald-50 border-emerald-400' : totalFieldReady > 0 ? 'bg-amber-50 border-amber-300' : 'bg-white border-[#1A4331]/15'}`} onClick={() => navigate('/trees/field')} data-testid="tree-dashboard-field-status">
            <div className="flex items-center gap-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${totalFieldReady >= FULL_LOAD ? 'bg-emerald-500' : totalFieldReady > 0 ? 'bg-amber-400' : 'bg-[#1A4331]/10'}`}>
                <Truck className={`w-5 h-5 ${totalFieldReady > 0 ? 'text-white' : 'text-[#4A5D53]'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-[#0A1711] font-['Outfit']">Field Status</h3>
                  {totalFieldReady >= FULL_LOAD && (
                    <span className="text-[10px] font-bold bg-emerald-500 text-white px-2 py-0.5 rounded-full" data-testid="tree-dashboard-full-load-badge">
                      {fullLoads} FULL LOAD{fullLoads > 1 ? 'S' : ''}
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#4A5D53]">
                  {totalFieldReady === 0 ? 'No pallets marked ready in field' : totalFieldReady >= FULL_LOAD ? `${totalFieldReady} pallets ready — ${fullLoads} full trailer${fullLoads > 1 ? 's' : ''}${fieldRemainder > 0 ? ` + ${fieldRemainder} extra` : ''}` : `${totalFieldReady} of ${FULL_LOAD} pallets ready — need ${FULL_LOAD - totalFieldReady} more for full load`}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {colours.filter(c => (fieldStatus[c.color] || 0) > 0).map(c => (
                  <div key={c.color} className="text-center">
                    <div className="w-3 h-3 rounded-full mx-auto mb-0.5" style={swatchStyle(c.color)} />
                    <span className="text-xs font-bold text-[#0A1711]">{fieldStatus[c.color]}</span>
                  </div>
                ))}
              </div>
              <div className="flex-shrink-0">
                <div className="w-14 h-14 rounded-xl bg-white/80 flex flex-col items-center justify-center border border-[#1A4331]/10">
                  <span className="text-xl font-bold text-[#0A1711] font-['Outfit'] leading-none">{totalFieldReady}</span>
                  <span className="text-[8px] text-[#4A5D53] uppercase tracking-wider">ready</span>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-[#4A5D53] flex-shrink-0" />
            </div>
            {totalFieldReady > 0 && (
              <div className="mt-3 w-full h-2 bg-white/60 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-500 ${totalFieldReady >= FULL_LOAD ? 'bg-emerald-500' : totalFieldReady >= FULL_LOAD * 0.7 ? 'bg-amber-400' : 'bg-[#1A4331]'}`} style={{ width: `${Math.min(100, (totalFieldReady / FULL_LOAD) * 100)}%` }} />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-[#1A4331]/15 rounded-xl p-5" data-testid="tree-stock-required">
              <h3 className="text-base font-semibold text-[#0A1711] font-['Outfit'] mb-3">Stock vs Required</h3>
              {fulfillmentSummary.length === 0 ? (
                <p className="text-sm text-[#4A5D53]">No upcoming collections.</p>
              ) : (
                <div className="space-y-2">
                  <div className="grid grid-cols-5 gap-3 text-[9px] uppercase tracking-wider font-bold text-[#4A5D53] pb-1 border-b border-[#1A4331]/10">
                    <span>Colour</span><span className="text-right">Need</span><span className="text-right">Have</span><span className="text-right">Short</span><span className="text-right">Status</span>
                  </div>
                  {fulfillmentSummary.map((f) => (
                    <div key={f.color} className={`grid grid-cols-5 gap-3 items-center py-2 px-2 rounded-lg text-sm ${f.ok ? 'bg-emerald-50/50' : 'bg-red-50'}`}>
                      <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full" style={swatchStyle(f.color)} />
                        <span className="font-semibold capitalize text-xs">{colours.find(c => c.color === f.color)?.label || f.color}</span>
                      </div>
                      <span className="text-right font-bold text-xs">{f.needed}</span>
                      <span className="text-right text-xs">{f.available}</span>
                      <span className={`text-right font-bold text-xs ${f.shortfall > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{f.shortfall > 0 ? `-${f.shortfall}` : '0'}</span>
                      <div className="flex justify-end">
                        {f.ok ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500" /> : <XCircle className="w-3.5 h-3.5 text-red-500" />}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white border border-[#1A4331]/15 rounded-xl p-5" data-testid="tree-hauler-stats">
              <h3 className="text-base font-semibold text-[#0A1711] font-['Outfit'] mb-3">Loads by Hauler</h3>
              {(!analytics?.hauler_stats?.length) ? (
                <p className="text-sm text-[#4A5D53]">No dispatch data yet.</p>
              ) : (
                <div className="space-y-2">
                  {analytics.hauler_stats.map((h, i) => {
                    const maxLoads = Math.max(...analytics.hauler_stats.map(x => x.loads));
                    const pct = maxLoads > 0 ? (h.loads / maxLoads) * 100 : 0;
                    return (
                      <div key={i} className="flex items-center gap-3">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <Building2 className="w-3.5 h-3.5 text-[#4A5D53] flex-shrink-0" />
                          <span className="text-xs font-medium text-[#0A1711] truncate">{h.hauler}</span>
                        </div>
                        <div className="w-32 h-4 bg-[#F4F1ED] rounded-full overflow-hidden flex-shrink-0">
                          <div className="h-full bg-[#1A4331] rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <div className="text-right flex-shrink-0 w-20">
                          <span className="text-xs font-bold text-[#0A1711]">{h.loads}</span>
                          <span className="text-[10px] text-[#4A5D53] ml-1">({h.pallets}p)</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white border border-[#1A4331]/15 rounded-xl p-5" data-testid="tree-activity-chart">
              <h3 className="text-base font-semibold text-[#0A1711] font-['Outfit'] mb-3">14-Day Activity</h3>
              {analytics?.daily_movements?.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={analytics.daily_movements} barGap={0}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(26,67,49,0.08)" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#4A5D53' }} interval={1} />
                    <YAxis tick={{ fontSize: 10, fill: '#4A5D53' }} />
                    <Tooltip contentStyle={{ fontSize: 12 }} />
                    <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="in" name="Pallets In" fill="#16a34a" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="out" name="Pallets Out" fill="#E27B58" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-48 flex items-center justify-center text-sm text-[#4A5D53]">No activity data yet</div>
              )}
            </div>

            <div className="bg-white border border-[#1A4331]/15 rounded-xl p-5" data-testid="tree-colour-demand">
              <h3 className="text-base font-semibold text-[#0A1711] font-['Outfit'] mb-3">Colour Demand</h3>
              {(!analytics?.colour_demand?.length) ? (
                <p className="text-sm text-[#4A5D53]">No demand data yet.</p>
              ) : (
                <div className="space-y-3">
                  {analytics.colour_demand.map((c, i) => {
                    const maxDemand = Math.max(...analytics.colour_demand.map(x => x.total));
                    const pct = maxDemand > 0 ? (c.total / maxDemand) * 100 : 0;
                    const stock = getStockForColour(c.color);
                    return (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-1.5">
                            <div className="w-2.5 h-2.5 rounded-full" style={swatchStyle(c.color)} />
                            <span className="text-xs font-semibold capitalize">{c.color}</span>
                          </div>
                          <span className="text-xs text-[#4A5D53]">{c.total} ordered &middot; {stock} in stock</span>
                        </div>
                        <div className="w-full h-3 bg-[#F4F1ED] rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: colourHex(c.color) }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white border border-[#1A4331]/15 rounded-xl overflow-hidden" data-testid="tree-scheduled-collections">
            <div className="px-5 py-3 border-b border-[#1A4331]/10 flex items-center justify-between">
              <h3 className="text-base font-semibold text-[#0A1711] font-['Outfit']">Scheduled Collections</h3>
              <Button variant="ghost" size="sm" onClick={() => navigate('/trees/loads')} className="text-[#1A4331] text-xs">
                Manage <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
            {upcomingCollections.length === 0 ? (
              <div className="py-10 text-center text-[#4A5D53]">
                <Calendar className="w-7 h-7 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No upcoming collections</p>
              </div>
            ) : (
              <div className="divide-y divide-[#1A4331]/5">
                {upcomingCollections.slice(0, 6).map((load) => {
                  const status = checkCollectionFulfillment(load.pallets_required);
                  const dateObj = load.departure_date ? new Date(load.departure_date + 'T00:00:00') : null;
                  return (
                    <div key={load.id} className="px-5 py-3 flex items-center gap-3 hover:bg-[#F4F1ED]/30 transition-colors">
                      <div className="w-11 h-11 rounded-lg bg-[#F4F1ED] flex flex-col items-center justify-center flex-shrink-0 border border-[#1A4331]/10">
                        <span className="text-[9px] uppercase tracking-wider text-[#4A5D53] font-bold leading-none">{dateObj?.toLocaleDateString('en-GB', { weekday: 'short' })}</span>
                        <span className="text-lg font-bold text-[#0A1711] font-['Outfit'] leading-none">{dateObj?.getDate()}</span>
                      </div>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${status === 'can_fulfill' ? 'bg-emerald-100' : 'bg-red-100'}`}>
                        {status === 'can_fulfill' ? <CheckCircle className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-red-600" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-[#0A1711] truncate">{load.driver_name}</p>
                        {load.destinations?.[0] && <p className="text-[10px] text-[#4A5D53] flex items-center gap-1"><MapPin className="w-2.5 h-2.5 text-[#E27B58]" /> {load.destinations[0].address}</p>}
                      </div>
                      <div className="flex flex-wrap gap-1 flex-shrink-0">
                        {(load.pallets_required || []).map((p, i) => (
                          <span key={i} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${getStockForColour(p.color) >= p.quantity ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                            {p.quantity}x {p.color}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
